// Landing page after the backend SSO callback. The backend has already set
// the auth cookies at this point — we just need to:
//   • refresh AuthContext so currentOrg / memberships populate
//   • show a quick "Linking your Google / Microsoft account" banner if the
//     user signed in with a new provider against an existing email, then
//     call /confirm-merge before redirecting
//   • otherwise redirect to /dashboard.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { GoogleLogo, MicrosoftOutlookLogo, CircleNotch } from '@phosphor-icons/react';

export default function SSOCallbackPage() {
  const navigate = useNavigate();
  const { API, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const merge = searchParams.get('merge') === '1';
  const provider = searchParams.get('provider');
  const email = searchParams.get('email');
  const name = searchParams.get('name');
  const [state, setState] = useState(merge ? 'prompt' : 'loading');
  const [error, setError] = useState('');

  // Straight-through case (no merge) — simply refresh session + go to dashboard.
  useEffect(() => {
    if (merge) return;
    (async () => {
      try { if (checkAuth) await checkAuth(); } catch { /* ignore */ }
      navigate('/dashboard', { replace: true });
    })();
  }, [merge, checkAuth, navigate]);

  const confirmMerge = async () => {
    setState('linking');
    try {
      await axios.post(`${API}/auth/sso/confirm-merge`, { provider }, { withCredentials: true });
      if (checkAuth) await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to link account');
      setState('prompt');
    }
  };

  const Icon = provider === 'microsoft' ? MicrosoftOutlookLogo : GoogleLogo;
  const providerLabel = provider === 'microsoft' ? 'Microsoft' : 'Google';

  if (state === 'loading' || state === 'linking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="flex items-center gap-3 text-sm">
          <CircleNotch className="animate-spin" size={18} />
          <span>{state === 'linking' ? `Linking your ${providerLabel} account…` : 'Signing you in…'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4" data-testid="sso-callback-merge">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-4">
          <Icon size={24} weight="bold" className="text-indigo-600" />
        </div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Link your {providerLabel} account?</h1>
        <p className="text-sm text-slate-600 mt-2">
          An Inspiro Projects account already exists for <strong className="text-slate-900">{email}</strong>.
          Link {providerLabel} to this account so you can sign in with one click next time.
        </p>
        {name && <p className="text-[12px] text-slate-400 mt-1">Signed in as: {name}</p>}
        {error && <p className="mt-3 text-sm text-red-600" data-testid="sso-merge-error">{error}</p>}
        <div className="mt-6 flex gap-2">
          <Button onClick={confirmMerge} data-testid="sso-merge-confirm" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
            Link {providerLabel}
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard', { replace: true })} data-testid="sso-merge-skip" className="flex-1">
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
